import classNames from 'classnames';

import { Image, type SelectOption } from '@jsmdg/yoshi';
import { HERO_STAGE_MOBILE_MEDIA } from '../../../shared/enums/heroStageMedia';
import { type CampaignCreative } from '../../../shared/types/campaignCreative';
import { type DynamicYieldCampaign } from '../../../shared/types/dynamicYieldCampaign';
import { type ImageSliderImage } from '../../../shared/types/pageContent';
import { DyHeroStageBanner } from './DyHeroStageBanner/DyHeroStageBanner';
import { ExperienceFinder } from './ExperienceFinder';
import { HeroStageBanner } from './HeroStageBanner/HeroStageBanner';
import styles from './HeroStage.module.scss';

type HeroStageProps = {
    readonly galleryImages: ImageSliderImage[];
    readonly categories: SelectOption[];
    readonly dynamicYieldHeroStageCreatives: DynamicYieldCampaign[];
    readonly heroStageCreatives: CampaignCreative[];
    readonly hasNavigationCreative: boolean;
    readonly verticalPosition: number;
};

const HeroStage = ({
    categories,
    dynamicYieldHeroStageCreatives,
    galleryImages,
    hasNavigationCreative,
    heroStageCreatives,
    verticalPosition,
}: HeroStageProps) => {
    const desktopImage = galleryImages.find(image => !image.isMobile) ?? galleryImages[0];
    const mobileImage =
        galleryImages.length > 1 ? galleryImages.find(image => image.isMobile) : undefined;

    const HeroStageContent = desktopImage && (
        <div
            className={classNames(
                styles.heroWrapper,
                'position-relative d-flex justify-content-center mb-5x mb-sm-0',
            )}
            data-testid="hero-stage"
        >
            <picture className="d-block w-100 h-100">
                {mobileImage && (
                    <source
                        media={HERO_STAGE_MOBILE_MEDIA}
                        srcSet={mobileImage.srcset || mobileImage.src}
                        sizes={mobileImage.sizes || undefined}
                    />
                )}
                <Image
                    alt="Hero stage"
                    className={classNames(styles.image, 'w-100 h-100')}
                    src={desktopImage.src}
                    srcSet={desktopImage.srcset || undefined}
                    sizes={desktopImage.sizes || undefined}
                    fetchPriority="high"
                    lazyLoad={false}
                />
            </picture>

            <div className={styles.curvedOverlay} />
            <div className={classNames(styles.overlay, 'position-absolute px-4x w-100')}>
                <ExperienceFinder
                    headline={desktopImage.headline}
                    mobileHeadline={mobileImage?.headline}
                    categories={categories}
                />
            </div>
        </div>
    );

    if (dynamicYieldHeroStageCreatives.length > 0) {
        const firstCreative = dynamicYieldHeroStageCreatives[0];

        return (
            <>
                <div
                    className={classNames(styles.heroStage, 'mb-0-5x')}
                    data-testid="hero-stage-banner"
                >
                    <DyHeroStageBanner
                        creative={firstCreative}
                        hasNavigationCreative={hasNavigationCreative}
                        verticalPosition={verticalPosition}
                    />
                </div>

                {HeroStageContent}
            </>
        );
    }

    return (
        <>
            {heroStageCreatives.length > 0 &&
                heroStageCreatives.map((creative, index) => {
                    return (
                        <div
                            className={styles.heroStage}
                            key={creative.placeholderId}
                            data-testid={`hero-stage-banner-${index}`}
                        >
                            <HeroStageBanner
                                creative={creative}
                                hasNavigationCreative={hasNavigationCreative}
                                verticalPosition={verticalPosition}
                            />
                        </div>
                    );
                })}

            {HeroStageContent}
        </>
    );
};

export { HeroStage };
